import { Switch } from '@headlessui/react';
import maplibregl from 'maplibre-gl';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../../../store/store';
import { fetchWithRetry } from '../../../../../utils/fetchWithRetry';
import { JWTHeader } from '../../../../../utils/JWTHeader';
import { getPolygonCentroid, lambert93ToWGS84 } from '../../../../../utils/mapUtils';
import { MapLibreProvider, useMapLibre } from '../../../../MapLibre/MapLibreContext';
import OverzoomTileLayer from '../../../../MapLibre/OverzoomTileLayer';

type GeoJSONCoordsRecursive = GeoJSON.Position | GeoJSONCoordsRecursive[];
interface SCIGeometry { type: string; coordinates: GeoJSONCoordsRecursive }
interface SCIPlotForMap { siren: string; plotId: string; geometry?: SCIGeometry }
interface CoproForMap { id: number; name: string; scis: { total: number; plots: SCIPlotForMap[] } }
interface SCIMapBaseProps { copros: CoproForMap[]; onParcelClick: (parcelId: string) => void }
interface SCIMapCoproProps extends SCIMapBaseProps { expandedCopro: number | null; setExpandedCopro: (coproId: number | null) => void; selectedParcel?: never; setSelectedParcel?: never }
interface SCIMapParcelProps extends SCIMapBaseProps { expandedCopro?: never; setExpandedCopro?: never; selectedParcel: string | null; setSelectedParcel: (parcelId: string | null) => void }

type SCIMapProps = SCIMapCoproProps | SCIMapParcelProps;

const colors = ['#FFFF00'];

// Convert incoming geometry (possibly Lambert-93) to WGS84 GeoJSON geometry
function convertGeometryToWgs84(geometry: SCIGeometry): GeoJSON.MultiPolygon | GeoJSON.Polygon {
  const isNumberPair = (v: unknown): v is [number, number] => Array.isArray(v) && typeof v[0] === 'number' && typeof v[1] === 'number';
  const toWgsPair = (pair: [number, number]): [number, number] => {
    const [x, y] = pair;
    // Heuristic: Lambert-93 values are far outside lon/lat ranges
    if (Math.abs(x) > 200 || Math.abs(y) > 200) {
      const { lat, lng } = lambert93ToWGS84(x, y);
      return [lng, lat];
    }
    return [x, y];
  };
  const deepConvert = (coords: GeoJSONCoordsRecursive): GeoJSONCoordsRecursive => {
    if (isNumberPair(coords)) { return toWgsPair(coords); }
    if (Array.isArray(coords)) { return coords.map((c) => deepConvert(c)); }
    return coords;
  };

  if (geometry.type === 'Polygon') {
    return { type: 'Polygon', coordinates: deepConvert(geometry.coordinates) as unknown as number[][][] };
  }
  return { type: 'MultiPolygon', coordinates: deepConvert(geometry.coordinates) as unknown as number[][][][] };
}

function PolygonLayer({
  geojson,
  color,
  selectedId,
  onClick,
}: {
  geojson: GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | GeoJSON.Polygon>;
  color: string;
  selectedId?: string | null;
  onClick?: (id: string) => void;
}) {
  const map = useMapLibre();
  const baseId = useMemo(() => `scis-polygons-${Math.random().toString(36).slice(2)}`, []);
  const sourceId = `${baseId}-src`;
  const fillId = `${baseId}-fill`;
  const outlineId = `${baseId}-outline`;
  const selectedFillId = `${baseId}-selected-fill`;
  const selectedOutlineId = `${baseId}-selected-outline`;

  // Install source and layers (even if style isn't loaded yet)
  useEffect(() => {
    if (!map) { return undefined; }

    let isUnmounted = false;
    const cleanupFns: Array<() => void> = [];
    const install = () => {
      if (!map || isUnmounted) { return; }
      if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded()) { return; }
      try {
        if (map.getLayer(selectedOutlineId)) { map.removeLayer(selectedOutlineId); }
        if (map.getLayer(selectedFillId)) { map.removeLayer(selectedFillId); }
        if (map.getLayer(outlineId)) { map.removeLayer(outlineId); }
        if (map.getLayer(fillId)) { map.removeLayer(fillId); }
        if (map.getSource(sourceId)) { map.removeSource(sourceId); }

        map.addSource(sourceId, { type: 'geojson', data: geojson } as maplibregl.GeoJSONSourceSpecification);
        map.addLayer({
          id: fillId,
          type: 'fill',
          source: sourceId,
          paint: { 'fill-color': color, 'fill-opacity': 0.25 },
        } as maplibregl.LayerSpecification);
        map.addLayer({
          id: outlineId,
          type: 'line',
          source: sourceId,
          paint: { 'line-color': color, 'line-width': 1.5, 'line-opacity': 0.8 },
        } as maplibregl.LayerSpecification);

        // Selected layers (initially may filter to none)
        map.addLayer({
          id: selectedFillId,
          type: 'fill',
          source: sourceId,
          paint: { 'fill-color': color, 'fill-opacity': 0.5 },
          filter: ['==', ['get', 'id'], selectedId ?? '__none__'],
        } as maplibregl.LayerSpecification);
        map.addLayer({
          id: selectedOutlineId,
          type: 'line',
          source: sourceId,
          paint: { 'line-color': '#000000', 'line-width': 2.5, 'line-opacity': 0.9 },
          filter: ['==', ['get', 'id'], selectedId ?? '__none__'],
        } as maplibregl.LayerSpecification);

        // Ensure polygon overlays are above rasters and 3D buildings
        try { map.moveLayer(fillId); } catch {}
        try { map.moveLayer(outlineId); } catch {}
        try { map.moveLayer(selectedFillId); } catch {}
        try { map.moveLayer(selectedOutlineId); } catch {}
      } catch {
        // ignore add errors (map destroyed during navigation, etc.)
      }

      // Click handling and hover cursor
      const handleClick = (e: maplibregl.MapLayerMouseEvent) => {
        if (!map || !map.isStyleLoaded()) { return; }
        const features = map.queryRenderedFeatures(e.point, { layers: [fillId, outlineId] });
        if (features.length > 0) {
          const id = (features[0].properties?.id as string) ?? null;
          if (id && onClick) { onClick(id); }
        }
      };
      const handleMouseMove = (e: maplibregl.MapLayerMouseEvent) => {
        if (!map || !map.isStyleLoaded()) { return; }
        const features = map.queryRenderedFeatures(e.point, { layers: [fillId, outlineId] });
        try { map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : ''; } catch {}
      };
      try {
        map.on('click', handleClick);
        map.on('mousemove', handleMouseMove);
      } catch {}

      // Return cleanup registered for after install
      cleanupFns.push(() => {
        try { map.off('click', handleClick); } catch {}
        try { map.off('mousemove', handleMouseMove); } catch {}
      });
    };


    if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded()) {
      try { map.once('load', install); } catch {}
      try { map.once('styledata', install); } catch {}
      cleanupFns.push(() => {
        try { map.off('load', install as (ev?: maplibregl.MapLibreEvent) => void); } catch {}
        try { map.off('styledata', install as (ev?: maplibregl.MapStyleDataEvent) => void); } catch {}
      });
    } else {
      install();
    }

    return () => {
      isUnmounted = true;
      // Run accumulated cleanups (events)
      cleanupFns.forEach((fn) => {
        try { fn(); } catch {}
      });
      // Remove layers/sources if present
      try { if (map.getLayer(selectedOutlineId)) { map.removeLayer(selectedOutlineId); } } catch {}
      try { if (map.getLayer(selectedFillId)) { map.removeLayer(selectedFillId); } } catch {}
      try { if (map.getLayer(outlineId)) { map.removeLayer(outlineId); } } catch {}
      try { if (map.getLayer(fillId)) { map.removeLayer(fillId); } } catch {}
      try { if (map.getSource(sourceId)) { map.removeSource(sourceId); } } catch {}
    };
  }, [map, geojson, color, sourceId, fillId, outlineId, selectedFillId, selectedOutlineId, baseId, onClick, selectedId]);

  // Update selection filters when selectedId changes
  useEffect(() => {
    if (!map) { return; }
    try {
      if (map.getLayer(String(selectedFillId))) { map.setFilter(selectedFillId, ['==', ['get', 'id'], selectedId ?? '__none__']); }
      if (map.getLayer(String(selectedOutlineId))) { map.setFilter(selectedOutlineId, ['==', ['get', 'id'], selectedId ?? '__none__']); }
    } catch {
      // ignore
    }
  }, [map, selectedId, selectedFillId, selectedOutlineId]);

  return null;
}

function CoproOutlineLayer({ geojson, color }: { geojson: GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | GeoJSON.Polygon>; color: string }) {
  const map = useMapLibre();
  const baseId = useMemo(() => `copro-outline-${Math.random().toString(36).slice(2)}`, []);
  const sourceId = `${baseId}-src`;
  const outlineId = `${baseId}-outline`;

  useEffect(() => {
    if (!map || typeof map.isStyleLoaded !== 'function' || !map.isStyleLoaded()) { return undefined; }
    try {
      if (map.getLayer(outlineId)) { map.removeLayer(outlineId); }
      if (map.getSource(sourceId)) { map.removeSource(sourceId); }
      map.addSource(sourceId, { type: 'geojson', data: geojson } as maplibregl.GeoJSONSourceSpecification);
      map.addLayer({
        id: outlineId,
        type: 'line',
        source: sourceId,
        paint: { 'line-color': color, 'line-width': 2.5, 'line-opacity': 0.8 },
      } as maplibregl.LayerSpecification);
      try { map.moveLayer(outlineId); } catch {}
    } catch {
      // ignore
    }

    return () => {
      try { if (map.getLayer(outlineId)) { map.removeLayer(outlineId); } } catch {}
      try { if (map.getSource(sourceId)) { map.removeSource(sourceId); } } catch {}
    };
  }, [map, sourceId, outlineId, geojson, color]);

  return null;
}

function PinIconsLayer({
  geojson,
  onClick,
}: {
  geojson: GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | GeoJSON.Polygon>;
  onClick?: (id: string) => void;
}) {
  const map = useMapLibre();
  const markersRef = useRef<Record<string, maplibregl.Marker>>({});

  useEffect(() => {
    if (!map) { return; }

    // Clear existing markers
    Object.values(markersRef.current).forEach((marker) => {
      try {
        marker.remove();
      } catch {
        // ignore
      }
    });
    markersRef.current = {};

    // Create markers for each parcel centroid
    geojson.features.forEach((feature) => {
      try {
        let coordinates: number[][] = [];

        if (feature.geometry.type === 'Polygon') {
          coordinates = feature.geometry.coordinates[0];
        } else if (feature.geometry.type === 'MultiPolygon') {
          // Use the largest polygon for centroid calculation
          coordinates = feature.geometry.coordinates.reduce((largest, current) => {
            const currentArea = current[0].length;
            const largestArea = largest.length;
            return currentArea > largestArea ? current[0] : largest;
          }, feature.geometry.coordinates[0][0]);
        }

        const centroid = getPolygonCentroid(coordinates);
        const plotId = feature.properties?.id;

        if (!plotId) { return; }

        // Create marker element (same style as CoproMap)
        const markerEl = document.createElement('div');
        markerEl.style.width = '18px';
        markerEl.style.height = '18px';
        markerEl.style.borderRadius = '50%';
        markerEl.style.background = '#FFFF00'; // Yellow color matching the polygon
        markerEl.style.boxShadow = '0 0 0 2px #fff inset, 0 1px 4px rgba(0,0,0,0.3)';
        markerEl.style.opacity = '0.85';
        markerEl.style.cursor = 'pointer';
        
        markerEl.addEventListener('click', () => {
          if (onClick) { onClick(plotId); }
        });

        const marker = new maplibregl.Marker({ element: markerEl, anchor: 'center' })
          .setLngLat([centroid.lng, centroid.lat])
          .addTo(map);

        markersRef.current[plotId] = marker;
      } catch (error) {
        console.error('Error creating marker:', error);
      }
    });

    return () => {
      Object.values(markersRef.current).forEach((marker) => {
        try {
          marker.remove();
        } catch {
          // ignore cleanup failures
        }
      });
      markersRef.current = {};
    };
  }, [map, geojson, onClick]);

  return null;
}

function FitToBounds({
  geojson,
  focusId,
}: {
  geojson: GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | GeoJSON.Polygon>;
  focusId?: string | null;
}) {
  const map = useMapLibre();
  const computeBbox = (fc: GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | GeoJSON.Polygon>) => {
    let minLng = Infinity; let minLat = Infinity; let maxLng = -Infinity; let maxLat = -Infinity;
    const pushCoord = (lng: number, lat: number) => {
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) { return; }
      minLng = Math.min(minLng, lng); minLat = Math.min(minLat, lat);
      maxLng = Math.max(maxLng, lng); maxLat = Math.max(maxLat, lat);
    };
    const scanCoords = (coords: GeoJSONCoordsRecursive | number) => {
      if (typeof coords === 'number') { return; }
      if (Array.isArray(coords) && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        pushCoord(coords[0], coords[1]);
      } else if (Array.isArray(coords)) {
        for (const c of coords) { scanCoords(c); }
      }
    };
    fc.features.forEach((f) => {
      if (focusId && f.properties?.id !== focusId) { return; }
      if (f.geometry) { scanCoords((f.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon).coordinates); }
    });
    if (minLng === Infinity) { return null; }
    return [[minLng, minLat], [maxLng, maxLat]] as [[number, number], [number, number]];
  };

  useEffect(() => {
    if (!map) { return; }
    const bbox = computeBbox(geojson);
    if (!bbox) { return; }
    try {
      map.fitBounds(bbox, { padding: 40, duration: 500 });
    } catch {
      // ignore fit errors
    }
  }, [map, geojson, focusId]);
  return null;
}

const SCIMap: React.FC<SCIMapProps> = ({ copros, onParcelClick, expandedCopro, selectedParcel, setSelectedParcel }) => {
  const [isCadastre, setIsCadastre] = useState(false);
  const [uniquePlots, setUniquePlots] = useState<Map<string, SCIPlotForMap>>(new Map());
  const [geometryByPlotId, setGeometryByPlotId] = useState<Map<string, SCIGeometry>>(new Map());
  const [coproOutlinePlotIds, setCoproOutlinePlotIds] = useState<string[]>([]);
  const [failedGeometryIds, setFailedGeometryIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let plots: SCIPlotForMap[] = [];
    if (expandedCopro) {
      const expandedCoproData = copros.find((copro) => copro.id === expandedCopro);
      if (expandedCoproData) { plots = expandedCoproData.scis.plots; }
    } else { plots = copros.flatMap((copro) => copro.scis.plots); }
    const plotMap = new Map<string, SCIPlotForMap>();
    plots.forEach((plot) => {
      if (plot?.plotId) {
        // Always include all plots, don't skip duplicates as they might be from different copros
        plotMap.set(plot.plotId, plot);
      }
    });
    setUniquePlots(plotMap);
  }, [copros, expandedCopro]);

  const jwt = useSelector((state: RootState) => state.auth.token);

  // When a copro is expanded, fetch all its plot ids to draw the outline
  useEffect(() => {
    let isCancelled = false;
    const fetchCoproPlots = async(id: number) => {
      try {
        const res = await fetch(`/api/copros/${id}`, JWTHeader(jwt));
        if (!res.ok) { return; }
        const data = await res.json();
        const plots = Array.isArray(data?.plots) ? data.plots : [];
        const ids: string[] = plots.map((p: { plot?: string | number }) => (typeof p?.plot === 'string' || typeof p?.plot === 'number') ? String(p.plot) : null).filter((v: string | null): v is string => !!v);
        if (!isCancelled) { setCoproOutlinePlotIds(ids); }
      } catch {
        // ignore
      }
    };
    if (expandedCopro) {
      fetchCoproPlots(expandedCopro);
    } else {
      setCoproOutlinePlotIds([]);
    }
    return () => { isCancelled = true; };
  }, [expandedCopro, jwt]);

  // Determine which plot geometries we need (SCIs + copro outline when expanded)
  const neededPlotIds = useMemo(() => {
    const sciIds = [...uniquePlots.keys()];
    const outlineIds = expandedCopro ? coproOutlinePlotIds : [];
    const all = new Set<string>([...sciIds, ...outlineIds]);
    return [...all.values()];
  }, [uniquePlots, coproOutlinePlotIds, expandedCopro]);

	// Determine if we must block map rendering until plots without geometry are fetched
	const pendingPlotIds = useMemo(() => {
		return neededPlotIds.filter((id) => {
			const hasInlineGeometry = !!uniquePlots.get(id)?.geometry;
			const hasFetchedGeometry = geometryByPlotId.has(id);
			const hasFailed = failedGeometryIds.has(id);
			// We consider it pending if it lacks any geometry and hasn't failed yet
			return !(hasInlineGeometry || hasFetchedGeometry || hasFailed);
		});
	}, [neededPlotIds, uniquePlots, geometryByPlotId, failedGeometryIds]);

	const isLoadingGeometries = pendingPlotIds.length > 0;

  // Fetch missing geometries from cadastre in a single batched call (chunked).
  // Previously one request per plot flooded nginx's rate limit (429); we now ask
  // the batch endpoint for every missing plot at once.
  useEffect(() => {
    if (!jwt) { return; }
    const missing = neededPlotIds.filter((id) => !geometryByPlotId.has(id) && !failedGeometryIds.has(id));
    if (missing.length === 0) { return; }
    let cancelled = false;

    const CHUNK_SIZE = 200;
    const chunks: string[][] = [];
    for (let i = 0; i < missing.length; i += CHUNK_SIZE) {
      chunks.push(missing.slice(i, i + CHUNK_SIZE));
    }

    const run = async() => {
      const results = new Map<string, SCIGeometry>();
      // Anything not returned by the server is considered failed (avoids re-fetch loops).
      const failed = new Set<string>(missing);
      const { headers: jwtHeaders } = JWTHeader(jwt);
      for (const chunk of chunks) {
        if (cancelled) { return; }
        try {
          // eslint-disable-next-line no-await-in-loop -- chunks are fetched sequentially on purpose to stay under the API rate limit
          const res = await fetchWithRetry('/api/cadastre/plots/batch', {
            method: 'POST',
            headers: { ...(jwtHeaders as Record<string, string> | undefined), 'Content-Type': 'application/json' },
            body: JSON.stringify({ plotIds: chunk }),
          });
          if (!res.ok) {
            console.warn(`Failed to fetch geometries batch: ${res.status} ${res.statusText}`);
            continue;
          }
          // eslint-disable-next-line no-await-in-loop -- part of the same sequential chunk processing
          const data = await res.json();
          if (Array.isArray(data)) {
            data.forEach((row: { id?: string; geometry?: unknown }) => {
              if (!row?.id) { return; }
              const geomRaw = row.geometry;
              const geom = typeof geomRaw === 'string' ? JSON.parse(geomRaw) : geomRaw;
              if (geom && typeof geom === 'object' && geom.type && geom.coordinates) {
                results.set(row.id, geom as SCIGeometry);
                failed.delete(row.id);
              }
            });
          }
        } catch (error) {
          console.error('Error fetching geometries batch:', error);
        }
      }
      if (cancelled) { return; }
      if (results.size > 0) {
        setGeometryByPlotId((prev) => {
          const m = new Map(prev);
          results.forEach((v, k) => { m.set(k, v); });
          return m;
        });
      }
      if (failed.size > 0) {
        setFailedGeometryIds((prev) => new Set([...prev, ...failed]));
      }
    };

    run();
    return () => { cancelled = true; };
  }, [neededPlotIds, jwt, failedGeometryIds]);

  const sciGeojson = useMemo<GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | GeoJSON.Polygon>>(() => {
    const features = [...uniquePlots.values()]
      .map((plot) => {
        const geom = geometryByPlotId.get(plot.plotId) || plot.geometry;
        return { id: plot.plotId, geom, plot };
      })
      .filter((x) => {
        // Only filter out plots that truly have no geometry data
        if (!x.geom) {
          console.warn(`Plot ${x.id} has no geometry data and will not be displayed on map`);
          return false;
        }
        return true;
      })
      .map((x) => {
        try {
          const convertedGeometry = convertGeometryToWgs84(x.geom as SCIGeometry);
          return {
            type: 'Feature' as const,
            properties: { id: x.id },
            geometry: convertedGeometry,
          };
        } catch (error) {
          console.error(`Failed to convert geometry for plot ${x.id}:`, error);
          return null;
        }
      })
      .filter((feature): feature is GeoJSON.Feature<GeoJSON.MultiPolygon | GeoJSON.Polygon, { id: string }> => feature !== null);

    return {
      type: 'FeatureCollection',
      features,
    };
  }, [uniquePlots, geometryByPlotId]);

  const coproOutlineGeojson = useMemo<GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | GeoJSON.Polygon>>(() => ({
    type: 'FeatureCollection',
    features: (expandedCopro ? coproOutlinePlotIds : [])
      .map((pid) => ({ id: pid, geom: geometryByPlotId.get(pid) }))
      .filter((x) => !!x.geom)
      .map((x) => ({
        type: 'Feature',
        properties: { id: x.id },
        geometry: convertGeometryToWgs84(x.geom as SCIGeometry),
      })),
  }), [expandedCopro, coproOutlinePlotIds, geometryByPlotId]);

  const selectedId = selectedParcel ?? null;



  // Stable combined features to avoid FitToBounds re-firing on every render/zoom
  const allFeaturesGeojson = useMemo<GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | GeoJSON.Polygon>>(() => ({
    type: 'FeatureCollection',
    features: [...coproOutlineGeojson.features, ...sciGeojson.features],
  }), [coproOutlineGeojson, sciGeojson]);


  const handleClick = (id: string) => {
    if (setSelectedParcel) {
      setSelectedParcel(id);
      return;
    }
    onParcelClick(id);
  };

	return (
		<div className="relative">
			{isLoadingGeometries ? (
				// Skeleton placeholder while fetching missing geometries
				<div className="h-[600px] w-full overflow-hidden rounded-md border border-slate-200 bg-slate-100">
					<div className="h-full w-full animate-pulse">
						<div className="h-10 bg-slate-200" />
						<div className="space-y-3 p-4">
							<div className="h-6 w-1/3 rounded bg-slate-200" />
							<div className="h-4 w-1/2 rounded bg-slate-200" />
							<div className="h-4 w-2/3 rounded bg-slate-200" />
						</div>
					</div>
				</div>
			) : (
				<>
					<MapLibreProvider center={[46.603354, 1.888334]} zoom={6} style={{ height: '600px', width: '100%' }} jwt={jwt} allowPitch={false} initialPitch={0} initialBearing={0}>
						<OverzoomTileLayer isCadastre={isCadastre} />
						{expandedCopro && coproOutlineGeojson.features.length > 0 && (
							<CoproOutlineLayer geojson={coproOutlineGeojson} color={'#555555'} />
						)}
						<PolygonLayer geojson={sciGeojson} color={colors[0]} selectedId={selectedId} onClick={handleClick} />
						<PinIconsLayer geojson={sciGeojson} onClick={handleClick} />
						<FitToBounds geojson={allFeaturesGeojson} focusId={selectedId ?? undefined} />
					</MapLibreProvider>
					<div className="absolute top-4 right-4 z-[1000] flex flex-col items-end space-y-2">
						<div className="flex items-center space-x-2 rounded-xl bg-white p-2 shadow-md">
							<div className="flex items-center">
								<Switch checked={isCadastre} onChange={() => setIsCadastre(!isCadastre)} className={`${ isCadastre ? 'bg-indigo-600' : 'bg-gray-200' } relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 focus:outline-none`}>
									<span aria-hidden="true" className={`${ isCadastre ? 'translate-x-5' : 'translate-x-0' } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} />
								</Switch>
								<span className="ml-3 text-sm font-medium text-gray-900">Cadastre</span>
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default SCIMap;
